import { CurrentUserService } from '../current-user/current-user.service';

/**
 * Routes that exist only to *perform* a step of the auth flow itself
 * (logging out, showing the post-logout confirmation screen, processing
 * an OAuth callback, or the iframe click-to-sign-in page) must never be
 * used as the page a user lands on *after* logging in. If one of these
 * ends up as the stored "return to" target -
 * e.g. because the browser was sitting on /logout (mid Grafana-logout call)
 * when a fresh interactive login got triggered - redirecting back to it
 * just re-enters that same flow. Landing back on /logout immediately
 * re-logs the user out via LogoutComponent's unconditional logout-on-mount,
 * making a single successful login look like it silently failed and has to
 * be repeated. See the logout->login loop bug reports from 2026-08-12/13.
 */
const AUTH_FLOW_ONLY_ROUTES = ['/logout', '/logged-out', '/auth/callback', '/auth/embed-login', '/auth/set-password'];

/**
 * Unauthenticated routes that must never kick off Auth0 login or a
 * `/client/home` bounce (which AuthGuard would then send to login).
 * Used by AuthInitializerService, AuthStateService, and the token interceptor.
 *
 * Prefix entries end with `/` so `/mobile/respond-prompt/...` matches but a
 * hypothetical `/mobile-admin` would not. Exact entries (`/logout`) match
 * the path itself and any nested path via `startsWith`.
 *
 * See #13276: anonymous members answering a prompt/survey were sent to the
 * S5 login page after the member-detail screen rendered, because a silent
 * token refresh on these public routes treated `login_required` as a
 * reason to navigate to `/client/home`.
 */
const PUBLIC_AUTH_ROUTES = [
  '/customer-stories/',
  '/s/',
  '/mobile/',
  '/r/',
  '/b/',
  '/t/',
  '/client/tracking/share/',
  '/logout',
  '/logged-out',
  '/access-denied',
  '/session-timeout',
  '/auth/callback',
  '/auth/embed-login',
  '/auth/login',
  '/auth/forgot-password',
  '/auth/set-password',
];

/**
 * True when this window is framed by another origin (Salesforce, etc.).
 * Auth0's authorize page sets CSP frame-ancestors 'none', so redirect login
 * cannot run inside the iframe; popup login can, but only from a user click.
 */
export function isRunningInIframe(): boolean {
  return typeof window !== 'undefined' && window !== window.top;
}

/**
 * True when `pathname` (defaults to the current window location) is a
 * public route that does not require authentication. Members answering a
 * mobile prompt or open survey must stay on these routes even if Auth0
 * reports `login_required`.
 */
export function isPublicAuthRoute(pathname?: string): boolean {
  const currentPath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return PUBLIC_AUTH_ROUTES.some(route => currentPath === route || currentPath.startsWith(route));
}

/**
 * Returns `target` unchanged unless it points at one of the auth-flow-only
 * routes above, in which case `fallback` (default `/client/home`) is
 * returned instead.
 */
export function sanitizeAuthTarget(target: string | null | undefined, fallback = '/client/home'): string {
  if (!target) {
    return fallback;
  }
  const path = target.split('?')[0].split('#')[0];
  if (AUTH_FLOW_ONLY_ROUTES.some(route => path === route || path.startsWith(`${route}/`))) {
    return fallback;
  }
  return target;
}

/**
 * Clear client-side auth state so the next login starts completely fresh.
 * Call this immediately before Auth0 logout to ensure cached tokens,
 * sessionStorage auth flow state, and the in-memory user cache are all reset.
 */
export function clearAuthLocalState(currentUserService: CurrentUserService): void {
  // Clear the in-memory user cache so the next login fetches fresh role data
  currentUserService.invalidateUserCache();

  // Clear any auth flow state left in sessionStorage
  const sessionKeys = [
    'sso_connection',
    'sso_prefer_connection',
    'auth_target_url',
    'auth_full_url',
    'auth_original_host',
    'auth_original_protocol',
    'auth_redirect_in_progress',
    'sso_auth_error'
  ];
  sessionKeys.forEach(key => sessionStorage.removeItem(key));

  // Remove Auth0 SDK localStorage keys if the SDK left them behind, plus the
  // self-hosted session token (#16114)
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('auth0.') || key.startsWith('@@auth0spajs@@') || key === 's5_access_token') {
      localStorage.removeItem(key);
    }
  }
}
